/**
 * Éditeur de template d'étiquette avec mode Simple et Visuel
 */

import { useState } from 'react';
import {
  LabelTemplate,
  CreateLabelTemplateDto,
  UpdateLabelTemplateDto,
  LabelTemplateType,
  FieldConfig,
  ALL_AVAILABLE_FIELDS,
} from '../../types/label-templates.types';
import { VisualLabelEditor } from './VisualLabelEditor';

interface Props {
  template: LabelTemplate | null;
  onSave: (data: CreateLabelTemplateDto | UpdateLabelTemplateDto) => void;
  onCancel: () => void;
}

type EditorMode = 'simple' | 'visual';

export function LabelTemplateEditor({ template, onSave, onCancel }: Props) {
  const [editorMode, setEditorMode] = useState<EditorMode>('simple');
  const [formData, setFormData] = useState({
    name: template?.name || '',
    type: template?.type || LabelTemplateType.SHELF,
    description: template?.description || '',
    width_mm: template?.width_mm || 50,
    height_mm: template?.height_mm || 25,
    dpi: template?.dpi || 203,
    darkness: template?.darkness || 25,
    print_speed: template?.print_speed || 4,
    is_default: template?.is_default || false,
    active: template?.active !== false,
  });

  const [fieldsConfig, setFieldsConfig] = useState<FieldConfig[]>(
    template?.fields_config || []
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      fields_config: fieldsConfig,
    });
  };

  const toggleFieldEnabled = (fieldId: string) => {
    setFieldsConfig(prev => {
      const existing = prev.find(f => f.id === fieldId);
      if (existing) {
        return prev.map(f =>
          f.id === fieldId ? { ...f, enabled: !f.enabled } : f
        );
      } else {
        // Ajouter le champ avec des valeurs par défaut
        const availableField = ALL_AVAILABLE_FIELDS.find(f => f.id === fieldId);
        if (!availableField) return prev;

        // Déterminer le suffixe automatique pour les champs de prix
        let defaultSuffix = '';
        if (availableField.type === 'currency') {
          if (fieldId === 'price_per_kg') {
            defaultSuffix = '€/kg';
          } else if (fieldId === 'price_per_unit') {
            defaultSuffix = '€/pce';
          } else {
            defaultSuffix = '€';
          }
        }

        const newField: FieldConfig = {
          id: fieldId,
          source: availableField.source || 'product',
          field: availableField.field,
          enabled: true,
          label: availableField.label,
          x: 2,
          y: prev.length * 6 + 2,
          fontSize: 10,
          fontWeight: 'normal',
          align: 'left',
          type: availableField.type,
          suffix: defaultSuffix || undefined,
          zIndex: prev.length + 1,
        };
        return [...prev, newField];
      }
    });
  };

  const isFieldEnabled = (fieldId: string) => {
    return fieldsConfig.find(f => f.id === fieldId)?.enabled || false;
  };

  return (
    <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 h-screen flex flex-col">
      <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {template ? 'Modifier Template' : 'Nouveau Template'}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Configurez les paramètres et les champs de l'étiquette
            </p>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-6 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Enregistrer
            </button>
          </div>
        </div>

        {/* Onglets */}
        <div className="border-b border-gray-200 mb-4">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              type="button"
              onClick={() => setEditorMode('simple')}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                ${
                  editorMode === 'simple'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              📋 Mode Simple
            </button>
            <button
              type="button"
              onClick={() => setEditorMode('visual')}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                ${
                  editorMode === 'visual'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              🎨 Mode Visuel
            </button>
          </nav>
        </div>

        {/* Contenu selon le mode */}
        {editorMode === 'simple' ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Paramètres généraux */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Paramètres Généraux</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du template *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as LabelTemplateType })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {Object.values(LabelTemplateType).map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Largeur (mm) *
                  </label>
                  <input
                    type="number"
                    required
                    min="10"
                    max="200"
                    value={formData.width_mm}
                    onChange={(e) => setFormData({ ...formData, width_mm: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hauteur (mm) *
                  </label>
                  <input
                    type="number"
                    required
                    min="10"
                    max="200"
                    value={formData.height_mm}
                    onChange={(e) => setFormData({ ...formData, height_mm: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    DPI
                  </label>
                  <input
                    type="number"
                    value={formData.dpi}
                    onChange={(e) => setFormData({ ...formData, dpi: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Darkness (0-30)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="30"
                    value={formData.darkness}
                    onChange={(e) => setFormData({ ...formData, darkness: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vitesse (1-5)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    value={formData.print_speed}
                    onChange={(e) => setFormData({ ...formData, print_speed: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Template par défaut</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Actif</span>
                </label>
              </div>
            </div>
          </div>

          {/* Champs disponibles */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Champs Disponibles</h2>

            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {ALL_AVAILABLE_FIELDS.map((field) => (
                <label
                  key={field.id}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                >
                  <div className="flex items-center flex-1">
                    <input
                      type="checkbox"
                      checked={isFieldEnabled(field.id)}
                      onChange={() => toggleFieldEnabled(field.id)}
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <div className="ml-3">
                      <span className="text-sm font-medium text-gray-900">{field.label}</span>
                      <span className="ml-2 text-xs text-gray-500">({field.type})</span>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400">{field.source}</span>
                </label>
              ))}
            </div>

            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-xs text-yellow-800">
                💡 <strong>Astuce:</strong> Les champs sont positionnés automatiquement.
                Pour une personnalisation avancée avec drag-and-drop, utilisez l'éditeur visuel (à venir).
              </p>
            </div>
          </div>
          </div>
        ) : (
          /* Mode Visuel */
          <div className="flex-1 bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
            <VisualLabelEditor
              widthMm={formData.width_mm}
              heightMm={formData.height_mm}
              dpi={formData.dpi}
              fields={fieldsConfig}
              onChange={setFieldsConfig}
            />
          </div>
        )}
      </form>
    </div>
  );
}
